import * as React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, LogOut, Menu, Moon, PanelLeftClose, PanelLeftOpen, Sun, Wifi, WifiOff } from 'lucide-react';

import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip } from '@/components/ui/tooltip';
import { ServerClock } from '@/app/shell/server-clock';
import { useSidebar } from '@/app/shell/sidebar/sidebar-context';
import { useSession } from '@/app/providers/session-provider';
import { useTheme } from '@/app/providers/theme-provider';
import { adminSocket, type SocketStatus } from '@/lib/socket';
import { cn } from '@/lib/utils';

function ConnectionPill() {
  const [status, setStatus] = React.useState<SocketStatus>(() => adminSocket.getStatus());
  React.useEffect(() => adminSocket.onStatus(setStatus), []);

  // Nothing is subscribed on pages without realtime, so hide rather than
  // reporting a scary "disconnected".
  if (status === 'closed') return null;

  const config = {
    open: { label: 'Live', tone: 'text-positive', Icon: Wifi },
    connecting: { label: 'Connecting', tone: 'text-ink-4', Icon: Wifi },
    reconnecting: { label: 'Reconnecting', tone: 'text-warn', Icon: WifiOff },
  }[status];

  return (
    <Tooltip label={`Realtime connection: ${config.label.toLowerCase()}`}>
      <span className={cn('flex items-center gap-1 text-[11px]', config.tone)}>
        <config.Icon className={cn('size-3.5', status !== 'open' && 'animate-pulse')} />
        <span className="hidden md:inline">{config.label}</span>
      </span>
    </Tooltip>
  );
}

export function Header({
  onOpenSidebar,
  titleSlotId,
  centerSlotId,
  actionsSlotId,
}: {
  onOpenSidebar: () => void;
  titleSlotId: string;
  centerSlotId: string;
  actionsSlotId: string;
}) {
  const { bootstrap } = useSession();
  const { theme, toggleTheme } = useTheme();
  const { collapsed, toggle } = useSidebar();
  const { user, role } = bootstrap;

  return (
    <header className="z-20 flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface/95 px-3 backdrop-blur">
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={onOpenSidebar}
        className="lg:hidden"
        aria-label="Open navigation"
      >
        <Menu />
      </Button>

      <Tooltip label={`${collapsed ? 'Expand' : 'Collapse'} sidebar (⌘B)`}>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={toggle}
          className="hidden lg:inline-flex"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        </Button>
      </Tooltip>

      <div id={titleSlotId} className="min-w-0 max-w-[40%] shrink-0" />
      {/* Page-owned centre content. Empty on most pages, where it simply keeps
          the actions pushed to the right edge. */}
      <div id={centerSlotId} className="flex min-w-0 flex-1 justify-center" />

      <div className="flex items-center gap-2">
        <div id={actionsSlotId} className="flex items-center gap-2" />
        <ConnectionPill />
        <ServerClock />

        <Tooltip label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          <Button variant="ghost" size="icon-sm" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === 'dark' ? <Sun /> : <Moon />}
          </Button>
        </Tooltip>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 rounded-full p-0.5 transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label="Account menu"
            >
              <Avatar initials={user.initials} color={user.avatar_color} size="sm" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <DropdownMenuLabel>Signed in</DropdownMenuLabel>
            <div className="px-2 pb-1.5">
              <p className="truncate text-[13px] font-medium text-ink">{user.display_name}</p>
              <p className="truncate text-[11px] text-ink-3">{user.email || user.username}</p>
              <p className="mt-1 text-[11px] text-ink-4">{role.label}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings/appearance">Appearance</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href="/django-admin/" target="_blank" rel="noreferrer">
                <ExternalLink />
                Legacy Django admin
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild destructive>
              <Link to="/logout">
                <LogOut />
                Sign out
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
